// Правила работы с файлами. SMY-30
//
// В общем ядре, потому что клиенту они нужны раньше сервера: экран 07
// должен сказать «файл слишком большой» до того, как человек потратит
// время на загрузку по мобильной сети.

export const FILE_KINDS = ['pdf', 'docx', 'jpeg', 'png'] as const
export type FileKind = (typeof FILE_KINDS)[number]

export interface FileTypeSpec {
  kind: FileKind
  mimeTypes: string[]
  extensions: string[]
  /** Потолок размера. Разный: договор на 14 страниц и фото — разные вещи. */
  maxBytes: number
  label: string
}

const MB = 1024 * 1024

export const FILE_TYPES: Record<FileKind, FileTypeSpec> = {
  pdf: { kind: 'pdf', mimeTypes: ['application/pdf'], extensions: ['.pdf'], maxBytes: 20 * MB, label: 'PDF' },
  docx: {
    kind: 'docx',
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    extensions: ['.docx'],
    maxBytes: 20 * MB,
    label: 'Word',
  },
  jpeg: { kind: 'jpeg', mimeTypes: ['image/jpeg'], extensions: ['.jpg', '.jpeg'], maxBytes: 15 * MB, label: 'JPEG' },
  png: { kind: 'png', mimeTypes: ['image/png'], extensions: ['.png'], maxBytes: 15 * MB, label: 'PNG' },
}

/** Абсолютный потолок — на случай, если тип не определился. */
export const MAX_UPLOAD_BYTES = Math.max(...Object.values(FILE_TYPES).map((t) => t.maxBytes))

/**
 * Суточный потолок загрузок на человека, окно скользящее. SMY-328
 *
 * Решение владельца 17.09.2026. Без потолка гостевой токен — а он бесплатен —
 * клал в хранилище сколько угодно: триста загрузок по 20 МБ в минуту, до шести
 * гигабайт, и хранятся они тридцать дней (`RETENTION_DAYS.upload`). MinIO стоит
 * на одной машине с базой, и кончившийся диск остановил бы и её.
 *
 * Пятьдесят файлов — половина месячного запаса разборов документов у «Про» за
 * один день; живой человек в это не упирается. Пятьсот мегабайт — двадцать пять
 * файлов предельного размера.
 *
 * Считаются только загрузки: результаты (`results/`) кладём мы сами, и за них
 * заплачено.
 */
export const DAILY_UPLOADS = {
  files: 50,
  bytes: 500 * MB,
  hours: 24,
} as const

/**
 * Сроки хранения. Решение владельца продукта от 03.09.2026.
 *
 * Загруженное и созданное разведены по смыслу, а не по числам.
 *
 * **Загрузки** — чужие документы: договоры, счета, фото. Свой договор у
 * пользователя и так есть, нам он нужен на время разбора и правок. Каждый
 * лишний день хранения — день ответственности без выгоды для продукта.
 *
 * **Результаты** — то, за что заплатили. Они часть истории и проектов
 * (экран 09) и живут, пока живёт аккаунт. Удалять их по таймеру, оставляя
 * карточку артефакта в проекте, значит показать пользователю битую плитку:
 * у artifacts срока нет, и он и не нужен. Удаляются вместе с аккаунтом —
 * это SMY-33.
 *
 * Ограничением диска сроки не являются: при десяти приглашённых места
 * хватает на годы (infra/capacity.md). Смысл срока — приватность.
 */
export const RETENTION_DAYS = {
  /** Загруженное пользователем: договоры, счета, фото. */
  upload: 30,
  /** Результаты не истекают по времени — только вместе с аккаунтом. */
  result: null,
} as const

export type RetentionKind = keyof typeof RETENTION_DAYS

/** `null` означает «не истекает»: срока у файла не будет, и уборка его не тронет. */
export function expiryFor(kind: RetentionKind, from: Date): Date | null {
  const days = RETENTION_DAYS[kind]
  return days == null ? null : new Date(from.getTime() + days * 24 * 60 * 60 * 1000)
}

/**
 * Предупреждение до загрузки — требование карты потоков данных: запрет на
 * государственную, банковскую, медицинскую и коммерческую тайну в
 * потребительском тарифе. Текст здесь, чтобы совпадал на всех платформах.
 */
export const UPLOAD_WARNING =
  'Не загружай документы с государственной, банковской, медицинской или коммерческой тайной — ' +
  'обычный тариф для них не подходит.'

export type UploadProblem =
  | { reason: 'too_large'; maxBytes: number }
  | { reason: 'unsupported_type' }
  | { reason: 'type_mismatch'; declared: string; detected: FileKind }
  | { reason: 'bad_filename' }

/**
 * Управляющие знаки — вся категория Cc Юникода: U+0000–U+001F и
 * U+007F–U+009F. 11.09.2026
 *
 * Имя файла приходит в заголовке multipart, то есть от кого угодно, и до этой
 * проверки доходило до базы как есть. Нулевой байт PostgreSQL в text не
 * принимает: запись строки `files` падала, ручка отвечала 500 с текстом
 * запроса, а объект к этому времени уже лежал в хранилище — без строки, по
 * которой его нашли бы уборка и удаление аккаунта. Остальные знаки база
 * принимала и хранила.
 *
 * Отклоняется диапазон целиком, хотя падает база только на нулевом байте. С
 * клавиатуры эти знаки не набираются и в имени не видны, так что имя с ними —
 * собранный руками запрос или битая кодировка, а не опечатка человека. Цена
 * отказа для того редкого, у кого такой файл есть, — переименовать его.
 *
 * Кириллицу диапазон не задевает, и это сверено, а не выведено (живьём
 * 11.09.2026 на `@fastify/multipart`): разбери сервер имя из сырых байтов
 * UTF-8 как latin1, у «р» второй байт стал бы U+0080, и отказ получил бы
 * каждый «договор.pdf». Сервер разбирает такое имя как UTF-8, и держит это
 * тест ручки (`apps/api/src/routes/files.test.ts`): обновление разборщика,
 * сменившее кодировку, покраснит его, а не молча закроет загрузку кириллице.
 */
const hasControlChar = (text: string): boolean => {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return true
  }
  return false
}

/** Проверка того, что известно до чтения содержимого. */
export function checkDeclared(filename: string, sizeBytes: number, mimeType: string): UploadProblem | null {
  // Имя первым: сломанное имя и есть причина отказа, а знак после расширения
  // ещё и прячет само расширение.
  if (hasControlChar(filename)) return { reason: 'bad_filename' }
  const spec = Object.values(FILE_TYPES).find(
    (t) => t.mimeTypes.includes(mimeType) || t.extensions.some((e) => filename.toLowerCase().endsWith(e)),
  )
  if (spec == null) return { reason: 'unsupported_type' }
  if (sizeBytes > spec.maxBytes) return { reason: 'too_large', maxBytes: spec.maxBytes }
  return null
}

export const humanSize = (bytes: number): string =>
  bytes >= MB ? `${Math.round(bytes / MB)} МБ` : `${Math.round(bytes / 1024)} КБ`
